import { useEffect, useMemo, useState } from 'react';
import { Header, Icon, Message, Segment } from 'semantic-ui-react';
import ReportBody from './ReportBody.jsx';

const keyOf = (r) => `${r.settlement_report_id}/${r.report_year}/${r.as_at_date}`;

/* Past reports: pick a report (ID and client) and a year to see the whole year. If the figures were never approved, Finance and then the
 * Account Manager can approve the year now, and the Excel / PDF can be downloaded to submit. */
export default function HistoricTab({ reports, approvals, user, busy, storage, loadFull, onApprove, onDownload, today = new Date() }) {
  const [reportId, setReportId] = useState('');
  const [year, setYear] = useState(null);
  const [model, setModel] = useState(null);
  const [error, setError] = useState(null);

  const choices = useMemo(() => {                                   // one entry per report id, named after its latest version
    const latest = new Map();
    reports.forEach((r) => { const c = latest.get(r.settlement_report_id); if (!c || r.as_at_date > c.as_at_date) latest.set(r.settlement_report_id, r); });
    return [...latest.values()].sort((a, b) => a.settlement_report_id.localeCompare(b.settlement_report_id));
  }, [reports]);
  const years = useMemo(() => [...new Set(reports.filter((r) => r.settlement_report_id === reportId).map((r) => r.report_year))].sort((a, b) => b - a), [reports, reportId]);
  const summary = useMemo(() => (reportId && year !== null
    ? reports.filter((r) => r.settlement_report_id === reportId && r.report_year === year).sort((a, b) => b.as_at_date.localeCompare(a.as_at_date))[0] || null
    : null), [reports, reportId, year]);
  const summaryKey = summary ? keyOf(summary) : null;

  function pickReport(id) {
    setReportId(id); setModel(null); setError(null);
    const ys = [...new Set(reports.filter((r) => r.settlement_report_id === id).map((r) => r.report_year))].sort((a, b) => b - a);
    setYear(ys.find((y) => y < today.getFullYear()) ?? ys[0] ?? null);        // the latest year that has finished, if there is one
  }

  useEffect(() => {
    if (!summaryKey) return undefined;
    let live = true;
    setModel(null); setError(null);
    loadFull(summary).then((m) => { if (live) setModel(m); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey, loadFull]);

  const sel = useMemo(() => ({ mode: 'annual', year: year ?? 0, quarter: 0, month: 0 }), [year]);

  return (
    <section aria-labelledby="historic-heading">
      <Header as="h2" id="historic-heading" className="titlebar">
        <Icon name="history" />
        <Header.Content>Historic<Header.Subheader>A report for a whole year. If it was never approved, approve it now and download it to submit</Header.Subheader></Header.Content>
      </Header>
      <div className="toolbar" role="group" aria-label="Choose the report and year">
        <label htmlFor="hist-report">Report</label>
        <select id="hist-report" className="ui dropdown" value={reportId} onChange={(e) => pickReport(e.target.value)}>
          <option value="">Select a report…</option>
          {choices.map((r) => <option key={r.settlement_report_id} value={r.settlement_report_id}>{r.settlement_report_id} – {r.client_name || 'No client name'}</option>)}
        </select>
        <label htmlFor="hist-year">Year</label>
        <select id="hist-year" className="ui dropdown" value={year ?? ''} disabled={!reportId} onChange={(e) => { setYear(Number(e.target.value)); setModel(null); }}>
          {!reportId && <option value="">–</option>}
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {error && <Message negative role="alert">{error}</Message>}
      {!reportId && <Segment placeholder><Header icon><Icon name="history" />Choose a report and a year to see it.</Header></Segment>}
      {reportId && !model && !error && <Message info role="status">Loading the report…</Message>}
      {model && (
        <ReportBody key={keyOf(model.report)} model={model} historic sel={sel} approvals={approvals} user={user} busy={busy} storage={storage}
          onApprove={onApprove} onDownload={onDownload} />
      )}
    </section>
  );
}
